// TODO: If you make edits to this file you must deal with these ESLint rules
/*eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';

import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Trans } from '@lingui/macro';

import { ADDITIONAL_CRITERIA } from '@zego/content/common.json';
import {
  BREAKPOINTS,
  black,
  disabledColor,
  getColWidthCalc,
  primaryColor,
  unit,
  unitTRBL,
  white,
} from '@zego/theme';
import { useTracking } from '@zego/tracking';

import H1 from '@zego/components/atoms/H1';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import ButtonLink from '@zego/components/button/ButtonLink';
import ContentContainer from '@zego/components/ContentContainer';
import CoverByWakam from '@zego/components/CoverByWakam';
import CriteriaListSection from '@zego/components/CriteriaListSection';
import Logo from '@zego/components/header/Logo';
import UberHero from '@zego/components/promotions/UberHero';
import ScrollToElement from '@zego/components/ScrollToElement';
import SectionDisplay from '@zego/components/SectionDisplay';
import VerticalHairline from '@zego/components/VerticalHairline';
import ZegoCopyright from '@zego/components/ZegoCopyright';

import TrustpilotColor from '@zego/static/images/TrustpilotColor.png';
import heroImage from '@zego/static/private-hire/hero-private-hire-v3.jpg';
import Uber from '@zego/static/svgs/partners/uber-nopad.svg';
import Prize from '@zego/static/svgs/Prize.svg';
import SecureFinanceBlack from '@zego/static/svgs/SecureFinanceBlack.svg';

const COVER_BY_WAKAM = {
  textColor: disabledColor(7),
  icon: SecureFinanceBlack,
};

const UberPageWrapper = ({ banner, children }) => {
  const { trackEvent } = useTracking();

  return (
    <>
      <View css={{ backgroundColor: white }}>
        {banner ? banner : null}
        <ContentContainer
          css={{
            zIndex: 1000,
            position: 'relative',
            [BREAKPOINTS.normal]: {
              paddingTop: 40, // dependent on banner height due to negative margin
            },
          }}
        >
          <View
            css={{
              flexGrow: 0,
              display: 'flex',
              alignItems: 'center',
              paddingTop: unit(3),
              fill: white,
            }}
          >
            <Logo css={{ width: 103, height: 28 }} />
            <Spacer width={unit(2)} />
            <VerticalHairline
              css={{
                display: 'block',
                marginLeft: unit(1),
                marginRight: unit(1),
              }}
              color={white}
              height="50px"
              thickness="2px"
            />
            <Spacer width={unit(2)} />
            <img
              alt="Uber Logo"
              css={{ display: 'inline-block', width: 78 }}
              src={Uber}
            />
          </View>
          <Spacer height={unit(3)} />
        </ContentContainer>

        <UberHero image={heroImage}>
          <View
            css={{
              [BREAKPOINTS.medium]: { width: getColWidthCalc(12, 20, 7) },
            }}
          >
            <Title>Get the policy that’s right for you</Title>
            <SubTitle>
              Zego’s Private Hire insurance is designed with Uber partners in
              mind.
            </SubTitle>
          </View>
        </UberHero>
        <View
          css={{
            backgroundColor: disabledColor(2),
            marginBottom: unit(6),
            [BREAKPOINTS.medium]: {
              padding: unitTRBL(8, 8),
            },
          }}
        >
          <ContentContainer
            css={{
              paddingBottom: unit(6),
              [BREAKPOINTS.medium]: {
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row-reverse',
                paddingBottom: 0,
              },
            }}
          >
            <View
              css={{
                width: '100%',
                position: 'relative',
                [BREAKPOINTS.medium]: {
                  width: getColWidthCalc(12, 20, 5),
                  marginTop: '-180px',
                },
              }}
            >
              {children}
              <Spacer height={unit(2)} />

              <CoverByWakam {...COVER_BY_WAKAM} />

              <View
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyItems: 'center',
                  marginLeft: getColWidthCalc(12, 20, 2),
                  paddingTop: unit(7.5),
                  [BREAKPOINTS.medium]: {
                    paddingTop: unit(2.5),
                  },
                }}
              >
                <img
                  src={TrustpilotColor}
                  alt="Trust Pilot Logo"
                  css={{ width: 105 }}
                />
                <VerticalHairline
                  css={{
                    display: 'block',
                    marginLeft: unit(2),
                    marginRight: unit(2),
                  }}
                  color={disabledColor(7)}
                  height="26px"
                />
                <RegularText
                  css={{
                    fontSize: 10,
                    color: black,
                    lineHeight: '16px',
                    position: 'relative',
                    top: '4px',
                  }}
                >
                  <Trans id="Global / Trust Pilot Review Percentage">
                    92% of our customers rate us 'great' or 'excellent'
                  </Trans>
                </RegularText>
              </View>
            </View>
            {/* This is left side */}
            <View
              css={{
                display: 'flex',
                flexDirection: 'column-reverse',
                [BREAKPOINTS.medium]: {
                  width: getColWidthCalc(12, 20, 7),
                  flexDirection: 'column',
                },
              }}
            >
              <View>
                <RegularText
                  css={{
                    color: disabledColor(10),
                    fontSize: '18px',
                    lineHeight: '24px',
                    marginTop: unit(10),
                    [BREAKPOINTS.medium]: {
                      marginTop: 0,
                    },
                  }}
                >
                  Zego’s three insurance options give you the best protection
                  at competitive prices.
                </RegularText>
                <Spacer height={unit(3)} />
                <RegularText
                  css={{
                    color: disabledColor(10),
                    fontSize: '22px',
                    lineHeight: '28px',
                    fontWeight: 'bold',
                  }}
                >
                  Simply choose your level of cover and policy type:
                </RegularText>
                <Spacer height={unit(4)} />
                <RegularText
                  css={{
                    color: disabledColor(10),
                    fontWeight: 500,
                    fontSize: '22px',
                    lineHeight: '28px',
                    textAlign: 'left',
                    margin: '0 auto',
                    [BREAKPOINTS.medium]: {
                      width: '100%',
                    },
                  }}
                >
                  7-day
                </RegularText>
                <RegularText
                  css={{
                    fontSize: '18px',
                    lineHeight: '24px',
                    color: disabledColor(8),
                    marginBottom: unit(5),
                    marginTop: unit(1),
                    [BREAKPOINTS.medium]: {
                      marginBottom: unit(3),
                      marginTop: unit(1),
                    },
                  }}
                >
                  Short-term cover with no long-term commitment.
                </RegularText>

                <RegularText
                  css={{
                    color: disabledColor(10),
                    fontWeight: 500,
                    fontSize: '22px',
                    lineHeight: '28px',
                    textAlign: 'left',
                    margin: '0 auto',
                    [BREAKPOINTS.medium]: {
                      width: '100%',
                    },
                  }}
                >
                  30-day
                </RegularText>
                <RegularText
                  css={{
                    fontSize: '18px',
                    lineHeight: '24px',
                    color: disabledColor(8),
                    marginBottom: unit(5),
                    marginTop: unit(1),
                    [BREAKPOINTS.medium]: {
                      marginBottom: unit(3),
                      marginTop: unit(1),
                    },
                  }}
                >
                  Short to medium-term cover for as long as you need it.
                </RegularText>
                <RegularText
                  css={{
                    color: disabledColor(10),
                    fontWeight: 500,
                    fontSize: '22px',
                    lineHeight: '28px',
                    textAlign: 'left',
                    margin: '0 auto',
                    [BREAKPOINTS.medium]: {
                      width: '100%',
                    },
                  }}
                >
                  Annual
                </RegularText>
                <RegularText
                  css={{
                    fontSize: '18px',
                    lineHeight: '24px',
                    color: disabledColor(8),
                    marginBottom: unit(5),
                    marginTop: unit(1),
                    [BREAKPOINTS.medium]: {
                      marginBottom: unit(3),
                      marginTop: unit(1),
                    },
                  }}
                >
                  Cost-effective cover for full-time drivers.
                </RegularText>

                <RegularText
                  css={{
                    color: disabledColor(10),
                    fontWeight: 500,
                    fontSize: 18,
                    lineHeight: '24px',
                    textAlign: 'left',
                    margin: '0 auto',
                    paddingBottom: unit(4.5),
                    [BREAKPOINTS.medium]: {
                      width: '100%',
                      paddingBottom: unit(2),
                    },
                  }}
                >
                  Zego&apos;s Private Hire insurance can offer:
                </RegularText>
                <View
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginLeft: '18px',
                    [BREAKPOINTS.medium]: {
                      flexDirection: 'row',
                    },
                  }}
                >
                  <ul>
                    <FeatureListItem>NCD protection</FeatureListItem>
                    <FeatureListItem>
                      Public Liability insurance
                    </FeatureListItem>
                    <FeatureListItem>Legal expenses cover</FeatureListItem>
                    <FeatureListItem>
                      Claims line available 24/7
                    </FeatureListItem>
                  </ul>
                  <ul
                    css={{
                      marginLeft: 0,
                      [BREAKPOINTS.medium]: {
                        marginLeft: unit(6.5),
                      },
                    }}
                  >
                    <FeatureListItem css={{ paddingRight: unit(1) }}>
                      Food/parcel delivery cover
                    </FeatureListItem>
                    <View
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <FeatureListItem css={{ paddingRight: unit(1) }}>
                        Breakdown Cover
                      </FeatureListItem>
                      <div css={{ display: 'flex', alignItems: 'center' }}>
                        <img src={Prize} alt="Prize icon" />
                        <RegularText
                          css={{
                            color: primaryColor(11),
                            fontSize: 10,
                            fontWeight: 'bold',
                            paddingLeft: unit(0.5),
                          }}
                        >
                          Exclusive for UBER
                        </RegularText>
                      </div>
                    </View>

                    <View
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <FeatureListItem css={{ paddingRight: unit(1) }}>
                        PCO car replacement
                      </FeatureListItem>
                      <div css={{ display: 'flex', alignItems: 'center' }}>
                        <img src={Prize} alt="Prize icon" />
                        <RegularText
                          css={{
                            color: primaryColor(11),
                            fontSize: 10,
                            fontWeight: 'bold',
                            paddingLeft: unit(0.5),
                          }}
                        >
                          Exclusive for UBER
                        </RegularText>
                      </div>
                    </View>
                    <FeatureListItem>
                      Discount for using a dash cam
                    </FeatureListItem>
                  </ul>
                </View>
              </View>
              <Spacer height={unit(3)} />
              <RegularText
                css={{
                  display: 'none',
                  [BREAKPOINTS.medium]: {
                    display: 'block',
                    fontWeight: 'bold',
                    fontSize: '20px',
                    lineHeight: '24px',
                    color: disabledColor(10),
                  },
                }}
              >
                Get your quick quote now and start saving with Zego!
              </RegularText>
            </View>
          </ContentContainer>
        </View>
        <CriteriaListSection
          title="Criteria"
          criteria={CRITERIA}
          footer={ADDITIONAL_CRITERIA}
        />
        <ContentContainer
          css={{
            paddingTop: unit(7),
            display: 'block',
            [BREAKPOINTS.medium]: {
              display: 'none',
            },
          }}
        >
          <RegularText
            css={{
              color: disabledColor(10),
              fontSize: 20,
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            Get your quick quote now and start saving with Zego!
          </RegularText>
          <ScrollToElement
            selector="#uber-landing-qq"
            render={scroll => (
              <ButtonLink
                onClick={event => {
                  event.preventDefault();
                  scroll();
                  trackEvent(
                    'Uber Landing Page, Get Quote Scroll to Top - Button Clicked',
                  );
                }}
                backgroundColor={primaryColor(8)}
                css={{
                  width: '100%',
                  marginLeft: 0,
                  textTransform: 'uppercase',
                  padding: '22px 107px 22px 107px',
                  fontSize: 14,
                  borderRadius: 0,
                  fontWeight: 'normal',
                  marginTop: unit(4),
                }}
              >
                Get Quote
              </ButtonLink>
            )}
          />
        </ContentContainer>
        <View
          css={{
            width: '50%',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginTop: unit(6),
          }}
        >
          <RegularText
            css={{
              fontSize: 10,
              lineHeight: '12px',
              textAlign: 'center',
              color: disabledColor(6),
            }}
          >
            *10% of our customers paid this or less in December 2018 and
            January 2019.
          </RegularText>
        </View>
      </View>

      <SectionDisplay>
        <ContentContainer>
          <div
            css={css`
              display: grid;
              grid-column-gap: 20px;
              grid-row-gap: ${unit(4)}px;
              grid-auto-rows: auto;
              grid-template-columns: [aside-start main-start] 1fr [aside-end main-end];
              @media screen and (min-width: 768px) {
                grid-template-columns: [aside-start] 2fr [aside-end main-start] 10fr [main-end];
              }
            `}
          >
            <div
              css={css`
                grid-column: aside;
              `}
            >
              <RegularText
                css={{
                  color: disabledColor(10),
                  fontSize: 10,
                  lineHeight: '13px',
                }}
              >
                <ZegoCopyright />
              </RegularText>
            </div>

            <div
              css={css`
                grid-column: main;
              `}
            >
              <RegularText css={{ fontSize: 10, lineHeight: '13px' }}>
                Zego is a trading name of Extracover Limited, which is
                authorised and regulated by the Financial Conduct Authority.
                <br />
                (FRN: 757871).
              </RegularText>

              <Spacer height={unit(1)} />

              <RegularText css={{ fontSize: 10, lineHeight: '13px' }}>
                ExtraCover Limited is registered in England and Wales, No
                10128841. Registered address: 5th floor, FORA Shoreditch, 21-33
                Great Eastern St, Hackney, London EC2A 3EJ.
              </RegularText>
            </div>
          </div>
        </ContentContainer>
      </SectionDisplay>
    </>
  );
};

const CRITERIA = [
  'Aged 21 to 75',
  '3 claims or fewer in the last 3 years',
  'Private hire licence',
  'England, Scotland & Wales only',
  'Full UK or EU driving licence',
  'Vehicle has a maximum of 9 seats',
];

const Title = styled(H1)({
  color: white,
  marginBottom: unit(3),
  fontSize: 35,
  [BREAKPOINTS.medium]: {
    fontSize: 53,
  },
});

const SubTitle = styled(RegularText)({
  color: white,
  fontSize: 18,
  marginBottom: unit(6),
  maxWidth: 700,
  fontWeight: 'bold',
  letterSpacing: '-0.94px',
  lineHeight: '32px',
  [BREAKPOINTS.medium]: {
    fontSize: 29,
  },
});

const FeatureListItem = styled('li')({
  listStyleType: 'disc',
  listStylePosition: 'outside',
  fontSize: 16,
  letterSpacing: '0.15px',
  lineHeight: '32px',
});

export default UberPageWrapper;
